import { FC, useState } from 'react'
import { Adjuster, Props as AdjusterProps } from './Adjuster'
import { Button } from 'components/buttons/Button'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import {
  convertSecondsFromMidnightIntoDateTime,
  getClockFromElapsedSecondsFromMidnight,
  getElapsedSecondsFromMidnight,
} from '@utils/time'
import {
  getClockFromSeconds,
  getMinuteTotal,
  isTimeDefined,
  nullTime,
  validate,
  getTimeAsDateTimeFromDateTime,
} from '../utils'
import { useLanguage } from '@hooks/useLanguage'
import { Title } from './Title'
import { DefinedTime } from '../types'

const ClearButton: typeof Button = ({ onClick }) => {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      className="text-sm font-medium text-neutral-400"
      onClick={onClick}
    >
      {t('components.timeWidget.clearTime')}
    </button>
  )
}

const SubmitButton: typeof Button = ({ disabled }) => {
  const { t } = useTranslation()

  return (
    <Button
      disabled={disabled}
      className="min-w-[6rem] text-sm"
      icon={faChevronRight}
      tabIndex={4}
    >
      {t('components.timeWidget.apply')}
    </Button>
  )
}

type TimeAdjusterProps = {
  variant: 'time' | 'time-and-duration'
  autoFocusedAdjuster: 'start' | 'end'
  dateEditing: DateTime
  displayMode: '12' | '24'
  endTimeSinceMidnight: number | null
  fieldPrefix?: string
  onSubmit: (
    startAtSecondsSinceMidnight: number | null,
    endAtSecondsSinceMidnight: number | null,
    timeZone: string,
  ) => void
  renderTopRight?: () => React.ReactNode
  startTimeSinceMidnight: number | null
  timeZone: string
  title: string
  minTime: DateTime
  maxTime: DateTime // Times entered between midnight and this time are interpreted as the next day
}

export const TimeAdjuster: FC<TimeAdjusterProps> = ({
  autoFocusedAdjuster,
  dateEditing,
  displayMode,
  endTimeSinceMidnight: endTimeSinceMidnightInitial,
  fieldPrefix,
  onSubmit,
  renderTopRight = () => null,
  startTimeSinceMidnight: startTimeSinceMidnightInitial,
  timeZone,
  title,
  variant,
  minTime,
  maxTime,
}) => {
  const { t } = useTranslation()

  const locale = useLanguage()
  const [startTimeSinceMidnight, setStartTimeSinceMidnight] = useState<
    number | null
  >(startTimeSinceMidnightInitial)
  const [endTimeSinceMidnight, setEndTimeSinceMidnight] = useState<
    number | null
  >(endTimeSinceMidnightInitial)
  const [duration, setDuration] = useState<number | null>(
    startTimeSinceMidnight === null || endTimeSinceMidnight === null
      ? null
      : endTimeSinceMidnight - startTimeSinceMidnight,
  )

  const { errors, isInvalid } = validate({
    startTime: startTimeSinceMidnight,
    endTime: endTimeSinceMidnight,
    relativeDate: dateEditing,
    minTime,
    maxTime,
    timeZone,
  })

  const minTimeInZone = minTime.setZone(timeZone)
  const maxTimeInZone = maxTime.setZone(timeZone)
  const localizedMinTime = minTimeInZone.toLocaleString(DateTime.TIME_SIMPLE, {
    locale,
  })
  const localizedMaxTime = maxTimeInZone.toLocaleString(DateTime.TIME_SIMPLE, {
    locale,
  })

  const startClock =
    startTimeSinceMidnight !== null
      ? getClockFromElapsedSecondsFromMidnight(
          startTimeSinceMidnight,
          dateEditing,
          timeZone,
        )
      : nullTime

  const endClock =
    endTimeSinceMidnight !== null
      ? getClockFromElapsedSecondsFromMidnight(
          endTimeSinceMidnight,
          dateEditing,
          timeZone,
        )
      : nullTime

  const durationClock =
    duration !== null ? getClockFromSeconds(duration) : nullTime

  const startDateTime =
    startTimeSinceMidnight !== null
      ? convertSecondsFromMidnightIntoDateTime(
          startTimeSinceMidnight,
          dateEditing,
          timeZone,
        )
      : null

  const endDateTime =
    endTimeSinceMidnight !== null
      ? convertSecondsFromMidnightIntoDateTime(
          endTimeSinceMidnight,
          dateEditing,
          timeZone,
        )
      : null

  const handleSubmit = () => {
    if (isInvalid) return

    onSubmit(startTimeSinceMidnight, endTimeSinceMidnight, timeZone)
  }

  const handleClear = () => {
    setStartTimeSinceMidnight(null)
    setEndTimeSinceMidnight(null)
    setDuration(null)
  }

  const handleStartTimeChange: AdjusterProps['onChange'] = (
    clockStartHour,
    clockStartMinute,
  ) => {
    const clockTime = { hour: clockStartHour, minute: clockStartMinute }

    if (!isTimeDefined(clockTime)) {
      setStartTimeSinceMidnight(null)
      return
    }

    const newStartTimeSinceMidnight =
      // If the start time is less than the min time,
      // interpret as the next day, otherwise interpret as the time card date
      getElapsedSecondsFromMidnight({
        clockHour: clockTime.hour,
        clockMinute: clockTime.minute,
        relativeDate: dateEditing,
        clockDate:
          getTimeAsDateTimeFromDateTime(clockTime, minTimeInZone) <
          minTimeInZone
            ? dateEditing.plus({ days: 1 })
            : dateEditing,
        timeZone,
      })

    setStartTimeSinceMidnight(newStartTimeSinceMidnight)

    if (duration !== null && endTimeSinceMidnight === null) {
      // If duration is set and end time was not set, set it now that we have duration and start time
      setEndTimeSinceMidnight(newStartTimeSinceMidnight + duration)
    } else if (endTimeSinceMidnight !== null && isTimeDefined(endClock)) {
      // Reinterpret end time if it was set now that start time is updated,
      // since the start time changes how the end time it is interpreted
      const newEndTimeSinceMidnight = setEndTimeSinceMidnightFromClock(
        endClock,
        newStartTimeSinceMidnight,
      )

      // Since end time was set and start time was updated, update the duration
      // (using the newly interpreted end time)
      setDuration(newEndTimeSinceMidnight - newStartTimeSinceMidnight)
    }
  }

  const setEndTimeSinceMidnightFromClock = (
    clockTime: DefinedTime,
    currentStartTimeSinceMidnight: number | null,
  ) => {
    const startTimeDateTime =
      currentStartTimeSinceMidnight !== null
        ? convertSecondsFromMidnightIntoDateTime(
            currentStartTimeSinceMidnight,
            dateEditing,
            timeZone,
          )
        : null

    // If the start time is not set, or it is set and is on the Time Card date
    // and the end time is after the start time:
    //   Interpret the end time as being on the Time Card date
    // Otherwise, interpret the end time as being on the next day
    const clockDate =
      startTimeDateTime === null ||
      (startTimeDateTime.hasSame(dateEditing, 'day') &&
        getTimeAsDateTimeFromDateTime(clockTime, startTimeDateTime) >
          startTimeDateTime)
        ? dateEditing
        : dateEditing.plus({ days: 1 })

    const newEndTimeSinceMidnight = getElapsedSecondsFromMidnight({
      clockHour: clockTime.hour,
      clockMinute: clockTime.minute,
      relativeDate: dateEditing,
      clockDate,
      timeZone,
    })

    setEndTimeSinceMidnight(newEndTimeSinceMidnight)
    return newEndTimeSinceMidnight
  }

  const handleEndTimeChange: AdjusterProps['onChange'] = (
    clockEndHour,
    clockEndMinute,
  ) => {
    const clockTime = { hour: clockEndHour, minute: clockEndMinute }

    if (!isTimeDefined(clockTime)) {
      setEndTimeSinceMidnight(null)
      return
    }

    const newEndTimeSinceMidnight = setEndTimeSinceMidnightFromClock(
      clockTime,
      startTimeSinceMidnight,
    )

    if (duration !== null && startTimeSinceMidnight === null) {
      // If duration is set and start time is not set,
      // set start time since we now have an end time
      setStartTimeSinceMidnight(newEndTimeSinceMidnight - duration)
    } else if (
      startTimeSinceMidnight !== null &&
      startTimeSinceMidnight <= newEndTimeSinceMidnight
    ) {
      // Otherwise, if we have a start time, and the new end time is after the start time,
      // update the duration
      setDuration(newEndTimeSinceMidnight - startTimeSinceMidnight)
    }
  }

  const handleDurationChange: AdjusterProps['onChange'] = (
    durationHour,
    durationMinute,
  ) => {
    const newDurationClock = { hour: durationHour, minute: durationMinute }
    const newDuration = isTimeDefined(newDurationClock)
      ? getMinuteTotal(newDurationClock) * 60
      : null
    setDuration(newDuration)

    if (newDuration === null) return

    if (startTimeSinceMidnight !== null) {
      // Set end time based on start time and new duration if we have a start time
      setEndTimeSinceMidnight(startTimeSinceMidnight + newDuration)
    } else if (endTimeSinceMidnight !== null) {
      // Set start time based on end time and new duration if we have an end time (but no start time)
      setStartTimeSinceMidnight(endTimeSinceMidnight - newDuration)
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}
    >
      <Title title={title}>{renderTopRight()}</Title>
      <div className="flex flex-row p-4 border-b-2 border-neutral-200 space-x-4">
        <Adjuster
          adjusting="start"
          displayMode={displayMode}
          fieldPrefix={fieldPrefix}
          errors={errors.start}
          clockHour={startClock.hour}
          clockMinute={startClock.minute}
          onChange={handleStartTimeChange}
          autoFocus={autoFocusedAdjuster === 'start'}
          tabIndex={1}
          nextDay={
            startDateTime?.hasSame(dateEditing.plus({ days: 1 }), 'day') ??
            false
          }
          nextDayTooltip={t(
            'components.timeWidget.thisTimeIsOnTheDayAfterTheTimeCardDate',
            {
              localizedMinTime,
              localizedMaxTime,
            },
          )}
        />
        <Adjuster
          assumePm={displayMode === '12'}
          adjusting="end"
          displayMode={displayMode}
          fieldPrefix={fieldPrefix}
          errors={errors.end}
          clockHour={endClock.hour}
          clockMinute={endClock.minute}
          onChange={handleEndTimeChange}
          autoFocus={autoFocusedAdjuster === 'end'}
          tabIndex={2}
          nextDay={
            endDateTime?.hasSame(dateEditing.plus({ days: 1 }), 'day') ?? false
          }
          nextDayTooltip={t(
            'components.timeWidget.thisTimeIsOnTheDayAfterTheTimeCardDate',
            {
              localizedMinTime,
              localizedMaxTime,
            },
          )}
        />
        <Adjuster
          adjusting="duration"
          defaultDialHour={1}
          displayMode="duration"
          fieldPrefix={fieldPrefix}
          hidden={variant === 'time'}
          clockHour={durationClock.hour}
          clockMinute={durationClock.minute}
          onChange={handleDurationChange}
          tabIndex={3}
          nextDay={false}
        />
      </div>
      <div className="flex flex-row justify-between px-4 pt-2">
        <ClearButton onClick={handleClear} />
        <SubmitButton disabled={isInvalid} />
      </div>
    </form>
  )
}
